/** ASK MY HELPER — the panel. Text only, chat-style.
 *
 *  THREE SHAPES, one component (see lib/HelperDockContext):
 *    overlay  the original pop-up — dimmed backdrop, tap outside to close;
 *    pinned   docked to the right edge with NO dim and NO blur, the portal
 *             narrowed beside it and still fully usable. On a phone it is a
 *             half-screen sheet at the bottom, leaving the screen above it
 *             usable, and it collapses itself rather than cover the field
 *             somebody is typing in;
 *    slim     drawn by HelperDock, not here.
 *
 *  Mounted once above the routes, so none of this is lost on a page change. */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsRight, Mic, MicOff, Pin, PinOff, Send, Settings2, Undo2, Volume2, VolumeX, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import i18n from '../../lib/i18n';
import { useAuth } from '../../lib/auth';
import * as sv from '../../lib/samVoice';
import * as sl from '../../lib/samListen';
import SamCharacter from './SamCharacter';
import { useHelperDock } from '../../lib/HelperDockContext';

const field = "rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm";
const btn = "rounded-xl border border-slate-300 dark:border-slate-600 px-3.5 py-2 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50";
const primary = "rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50";

const Steps = ({ entry, t }) => (
    <div className="space-y-2" data-testid="helper-howto">
        <p className="text-sm font-semibold">{entry.name}</p>
        <p className="text-xs text-slate-500">{t('helper.whereToFind')}: {entry.where}</p>
        <ol className="space-y-1.5">
            {(entry.steps || []).map((s, i) => (
                <li key={i} className="flex gap-2 text-sm">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px] font-bold grid place-items-center mt-0.5">{i + 1}</span>
                    <span>{s}</span>
                </li>
            ))}
        </ol>
        {!!(entry.watch_out || []).length && (
            <div className="pt-1">
                <p className="text-xs font-semibold text-amber-500">{t('helper.watchOut')}</p>
                {entry.watch_out.map((w, i) => (
                    <p key={i} className="text-xs text-slate-500">{w}</p>
                ))}
            </div>
        )}
        {entry.confirmed === false && (
            <p className="text-xs text-amber-500" data-testid="helper-not-confirmed">
                {t('helper.notConfirmed')}
            </p>
        )}
    </div>
);

export const HelperPanel = ({ open, onClose }) => {
    const { t } = useTranslation();
    const dock = useHelperDock();
    const close = onClose || dock.close;
    const [cfg, setCfg] = useState(null);
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [pending, setPending] = useState(null);   // {organization, mode}
    const [preview, setPreview] = useState(null);
    const [run, setRun] = useState(null);
    // The shared draft frame: a draft held in the screen only, saved through
    // the function's own endpoint, with one tap to undo. Part 3 reuses this.
    const [gift, setGift] = useState(null);
    const [giftSaved, setGiftSaved] = useState(null);
    const [survey, setSurvey] = useState(null);
    const [surveySaved, setSurveySaved] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    // WHICH organization the on/off flag and the budget belong to. Empty means
    // the platform default row, the one a new organization inherits.
    const [orgId, setOrgId] = useState('');
    // The list is long and full of old test rows, so it is searchable and the
    // test rows stay hidden until asked for. One tap each way.
    const [orgFind, setOrgFind] = useState('');
    const [showTest, setShowTest] = useState(false);
    const endRef = useRef(null);
    // ── SAM ──────────────────────────────────────────────────────────────
    // His face, his voice, and the microphone. Everything here is optional:
    // with the speaker off, or no voices on the device, the text is unchanged.
    const { user } = useAuth();
    const uid = user?.id || user?.email || 'anon';
    const [mood, setMood] = useState('calm');
    const [speaking, setSpeaking] = useState(false);
    const [listening, setListening] = useState(false);
    const [heard, setHeard] = useState('');
    const [micNote, setMicNote] = useState('');
    const [rest, setRest] = useState('');
    const [voices, setVoices] = useState([]);
    const [prefs, setPrefs] = useState(() => sv.readPrefs(uid));
    const micRef = useRef(null);
    const unlocked = useRef(false);
    const samName = cfg?.sam_name || 'Sam';
    const samShown = (cfg?.sam_picture || 'sam') !== 'none';
    // What the gear is editing right now, before Save. Empty means "as saved".
    const [nameDraft, setNameDraft] = useState({});
    const samShownDraft = cfg?.sam_picture || 'sam';

    useEffect(() => { setPrefs(sv.readPrefs(uid)); }, [uid]);
    useEffect(() => {
        const sync = () => setPrefs(sv.readPrefs(uid));
        window.addEventListener('sam-prefs', sync);
        return () => window.removeEventListener('sam-prefs', sync);
    }, [uid]);
    // The mic tapped on the slim bar: it brings the panel back and opens here.
    useEffect(() => {
        if (dock.micPing) startMic();
    }, [dock.micPing]);  // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!sv.canSpeak()) return undefined;
        const load = () => setVoices(sv.listVoices());
        load();
        window.speechSynthesis.addEventListener?.('voiceschanged', load);
        return () => window.speechSynthesis.removeEventListener?.('voiceschanged', load);
    }, []);

    const savePrefs = (patch) => {
        const next = { ...prefs, ...patch };
        setPrefs(next);
        sv.writePrefs(uid, next);
        return next;
    };

    const chosenVoice = () => voices.find((v) => v.voiceURI === prefs.uri)
        || sv.pickDefault(voices);

    /** iPad will not speak unless speech started inside a real tap, so the
     *  first tap on Send, the mic or the speaker opens the door once. */
    const unlock = () => {
        if (unlocked.current || !sv.canSpeak()) return;
        unlocked.current = true;
        try {
            const u = new window.SpeechSynthesisUtterance(' ');
            u.volume = 0;
            window.speechSynthesis.speak(u);
        } catch (_e) { /* nothing lost: the text is on screen anyway */ }
    };

    const hush = () => { sv.stop(); setSpeaking(false); setRest(''); };

    /** He says the first two sentences; anything longer waits behind one tap. */
    const sayAloud = (what, nextMood = 'happy') => {
        setMood(nextMood);
        if (!prefs.speaker || !sv.canSpeak()) return;
        const all = sv.sentences(what);
        const now = all.slice(0, 2).join(' ');
        setRest(all.slice(2).join(' '));
        sv.speak(now, {
            voice: chosenVoice(), rate: prefs.rate, pitch: prefs.pitch,
            lang: (i18n.language || 'en').slice(0, 2),
            onStart: () => setSpeaking(true),
            onEnd: () => {
                setSpeaking(false);
                setTimeout(() => setMood('calm'), 900);
                if (prefs.keepListening) setTimeout(() => startMic(true), sl.SETTLE_AFTER_SPEECH);
            },
        });
    };

    const readRest = () => {
        const more = rest;
        setRest('');
        if (!more) return;
        sv.speak(more, {
            voice: chosenVoice(), rate: prefs.rate, pitch: prefs.pitch,
            lang: (i18n.language || 'en').slice(0, 2),
            onStart: () => setSpeaking(true),
            onEnd: () => { setSpeaking(false); setTimeout(() => setMood('calm'), 900); },
        });
    };

    /** What he reads out for an answer: the register entry in short form, or
     *  the note. The text on screen is never shortened. */
    const voiceOf = (body) => {
        if (body.entry) {
            return `${body.entry.name}. ${t('helper.whereToFind')}: ${body.entry.where}. `
                + (body.entry.steps || []).join(' ');
        }
        return body.note || '';
    };

    const startMic = async (auto = false) => {
        if (listening) { micRef.current?.stop(); return; }
        unlock();
        setMicNote('');
        if (!sl.canListen()) { setMicNote(t('sam.micUnsupported')); return; }
        if (!auto) {
            const ok = await sl.prime();
            if (!ok) { setMicNote(t('sam.micBlocked')); return; }
        }
        setHeard('');
        setListening(true);
        setMood('thinking');
        micRef.current = sl.listen({
            lang: (i18n.language || 'en').slice(0, 2),
            onInterim: (words) => setHeard(words),
            onFinal: (words) => {
                setListening(false); setHeard('');
                ask(words);                       // actions still only PREVIEW
            },
            onEnd: () => { setListening(false); setHeard(''); setMood('calm'); },
            onBlocked: (why) => {
                setListening(false); setMood('calm');
                setMicNote(why === 'unsupported' ? t('sam.micUnsupported') : t('sam.micBlocked'));
            },
        });
    };

    useEffect(() => {
        if (!open) return;
        api.get('/helper/config', { params: orgId ? { organization_id: orgId } : {} })
            .then((res) => setCfg(res.data));
    }, [open, orgId]);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); },
        [msgs, preview, run]);

    // PHONE, PINNED. The sheet must never sit on top of the box somebody is
    // typing in: if the thing that just took focus is underneath it, the panel
    // collapses to the bar instead of covering it.
    const pinned = dock.mode === 'pinned';
    useEffect(() => {
        if (!(pinned && dock.isPhone)) return undefined;
        const onFocus = (e) => {
            const el = e.target;
            if (!el || !el.getBoundingClientRect) return;
            if (el.closest && el.closest('[data-testid="helper-panel"]')) return;
            const box = el.getBoundingClientRect();
            if (box.bottom > window.innerHeight * 0.5) dock.slim();
        };
        window.addEventListener('focusin', onFocus);
        return () => window.removeEventListener('focusin', onFocus);
    }, [pinned, dock.isPhone]);  // eslint-disable-line react-hooks/exhaustive-deps

    if (!open) return null;

    const orgList = (cfg?.organizations || []).filter((o) => {
        if (!showTest && o.looks_test && o.id !== orgId) return false;
        const q = orgFind.trim().toLowerCase();
        return !q || o.name.toLowerCase().includes(q);
    });

    const say = (role, body) => setMsgs((m) => [...m, { role, ...body }]);

    const ask = async (raw) => {
        const q = (raw ?? text).trim();
        if (!q) return;
        say('me', { text: q });
        setText('');
        setBusy(true);
        hush();
        setMood('thinking');
        setPreview(null);
        setRun(null);
        setGift(null);
        setGiftSaved(null);
        setGift(null);
        setGiftSaved(null);
        setSurvey(null);
        setSurveySaved(null);
        try {
            const res = await api.post('/helper/ask',
                { text: q, organization_id: orgId || null });
            const d = res.data;
            if (d.kind === 'howto') { say('helper', { entry: d.entry }); sayAloud(voiceOf({ entry: d.entry }), 'happy'); }
            else if (d.kind === 'ask') { say('helper', { askField: d.field, options: d.options }); setMood('thinking'); }
            else if (d.kind === 'action' && d.action === 'survey_draft') {
                const pv = await api.post('/helper/action/survey/preview', {
                    organization_id: d.organization.id, name: d.survey.name,
                    questions: d.survey.questions, lang: d.lang,
                });
                setSurvey({
                    ...pv.data, organization: d.organization,
                    form: { name: pv.data.draft.name, questions: [...pv.data.draft.questions], lang: pv.data.draft.lang },
                });
            }
            else if (d.kind === 'action' && d.action === 'gift_card') {
                const pv = await api.post('/helper/action/gift-card/preview', {
                    organization_id: d.organization.id,
                    title: d.gift.title, value: d.gift.value,
                    gift_type: d.gift.gift_type, quantity: d.gift.quantity,
                });
                setGift({
                    ...pv.data, organization: d.organization,
                    form: { ...pv.data.draft, setup_id: pv.data.setup.id, description: '' },
                });
                if ((pv.data.needs || []).includes('title')) {
                    say('helper', { note: t('helper.whichGiftName') });
                }
            }
            else if (d.kind === 'action') {
                setPending({ organization: d.organization, mode: d.mode });
                const pv = await api.post('/helper/action/card-mode/preview',
                    { organization_id: d.organization.id, mode: d.mode });
                setPreview({ ...pv.data, organization: d.organization, mode: d.mode });
            } else if (d.kind === 'off') {
                const note = d.say === 'helper_no_key' ? t('helper.noKey') : t('helper.off');
                say('helper', { note });
                sayAloud(note, 'concerned');
            }
            else if (d.kind === 'capped') { say('helper', { note: t('helper.capped') }); sayAloud(t('helper.capped'), 'concerned'); }
            else { say('helper', { note: t('helper.unknown') }); sayAloud(t('helper.unknown'), 'concerned'); }
        } catch (e) {
            say('helper', { note: t('helper.unknown') });
            setMood('concerned');
        } finally { setBusy(false); }
    };

    const apply = async () => {
        setBusy(true);
        try {
            const res = await api.post('/helper/action/card-mode/apply', {
                organization_id: preview.organization.id, mode: preview.mode,
            });
            setRun({ ...res.data, organization: preview.organization, mode: preview.mode });
            setPreview(null);
            toast.success(t('helper.applied'));
            sayAloud(t('helper.applied'), 'celebrate');
        } catch (e) {
            toast.error(t('helper.unknown'));
        } finally { setBusy(false); }
    };

    const undo = async (card) => {
        setBusy(true);
        try {
            const res = await api.post('/helper/action/card-mode/undo',
                { run_id: run.run_id, card: card || null });
            setRun((r) => ({
                ...r,
                rows: r.rows.map((row) => ((!card || row.card === card)
                    && !res.data.skipped.find((s) => s.card_id === row.card_id)
                    ? { ...row, undone: true } : row)),
                skipped: res.data.skipped,
            }));
            if (res.data.skipped.length) toast.warning(t('helper.undoSkipped'));
            else { toast.success(t('helper.undone')); sayAloud(t('helper.undone'), 'celebrate'); }
        } finally { setBusy(false); }
    };

    const saveFlag = async (patch) => {
        const res = await api.put('/helper/config',
            { ...patch, organization_id: orgId || null });
        setCfg(res.data);
    };

    const shell = pinned
        ? (dock.isPhone
            // Phone: a half-screen sheet, the top half of the screen left alone.
            ? 'fixed inset-x-0 bottom-0 h-[50vh] z-[59] border-t'
            // Tablet and desktop: docked to the right edge, no dim, no blur.
            : 'fixed right-0 top-0 bottom-0 z-[59] border-l')
        : 'fixed z-[61] inset-x-0 bottom-0 top-16 sm:inset-y-0 sm:top-0 sm:right-0 sm:left-auto sm:w-[420px] sm:border-l';

    return (
        <>
            {/* The dim is for the pop-up ONLY. Pinned has none, which is the
                whole point: the portal stays readable and tappable. */}
            {!pinned && (
                <button type="button" aria-label={t('helper.close')} onClick={close}
                        data-testid="helper-backdrop"
                        className="fixed inset-0 z-[60] bg-slate-950/40 backdrop-blur-[2px]" />
            )}
            <aside data-testid="helper-panel"
                   style={(pinned && !dock.isPhone) ? { width: `${dock.width}px` } : undefined}
                   className={`${shell} bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-2xl flex flex-col`}>
                <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                    {samShown && (
                        <SamCharacter mood={listening ? 'thinking' : mood} speaking={speaking}
                                      size="panel" name={samName} onTap={hush}
                                      className="w-11 h-12 shrink-0" />
                    )}
                    <span className="flex-1 text-base md:text-lg font-semibold" data-testid="sam-name">{samName}</span>
                    {/* SPEAKER — always here. Off means text only. */}
                    <button type="button" className={btn} data-testid="sam-speaker"
                            onClick={() => {
                                unlock();
                                const next = savePrefs({ speaker: !prefs.speaker });
                                if (!next.speaker) hush();
                            }}
                            aria-pressed={prefs.speaker}
                            aria-label={prefs.speaker ? t('sam.speakerOff') : t('sam.speakerOn')}
                            title={prefs.speaker ? t('sam.speakerOff') : t('sam.speakerOn')}>
                        {prefs.speaker ? <Volume2 className="w-4 h-4" aria-hidden="true" />
                                       : <VolumeX className="w-4 h-4" aria-hidden="true" />}
                    </button>
                    {/* MIC — one tap to talk, a second tap to stop. */}
                    <button type="button" className={`${btn} ${listening ? 'text-rose-600' : ''}`}
                            data-testid="sam-mic" onClick={() => startMic()}
                            aria-pressed={listening}
                            aria-label={listening ? t('sam.micStop') : t('sam.micStart')}
                            title={listening ? t('sam.micStop') : t('sam.micStart')}>
                        {listening ? <MicOff className="w-4 h-4" aria-hidden="true" />
                                   : <Mic className="w-4 h-4" aria-hidden="true" />}
                    </button>
                    {/* PIN / UNPIN — one tap, same place, either way. */}
                    <button type="button" className={btn} data-testid="helper-pin"
                            onClick={() => (pinned ? dock.unpin() : dock.pin())}
                            aria-label={pinned ? t('helper.dock.unpin') : t('helper.dock.pin')}
                            title={pinned ? t('helper.dock.unpin') : t('helper.dock.pin')}>
                        {pinned ? <PinOff className="w-4 h-4" aria-hidden="true" />
                                : <Pin className="w-4 h-4" aria-hidden="true" />}
                    </button>
                    {pinned && (
                        <button type="button" className={btn} data-testid="helper-slim"
                                onClick={dock.slim}
                                aria-label={t('helper.dock.slim')} title={t('helper.dock.slim')}>
                            <ChevronsRight className="w-4 h-4" aria-hidden="true" />
                        </button>
                    )}
                    <button type="button" className={btn} onClick={() => setShowSettings((v) => !v)}
                            aria-label={t('helper.settings')} data-testid="helper-settings-btn">
                        <Settings2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                    {/* While pinned nothing is ever fully closed: this collapses
                        to the bar. Unpinned it closes, exactly as before. */}
                    <button type="button" className={btn}
                            onClick={() => (pinned ? dock.slim() : close())}
                            aria-label={pinned ? t('helper.dock.slim') : t('helper.close')}
                            data-testid="helper-close">
                        <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                </header>

                {showSettings && cfg && (
                    <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 space-y-2.5 overflow-y-auto max-h-[60%]"
                         data-testid="helper-settings">
                        {/* ── SAM: his name and his picture, per organization ── */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2.5">
                            <div className="flex items-center gap-3">
                                {(nameDraft.picture ?? samShownDraft) !== 'none' ? (
                                    <SamCharacter mood="happy" size="panel" name={nameDraft.name ?? samName}
                                                  className="w-10 h-12 shrink-0" />
                                ) : (
                                    <span className="w-10 h-12 shrink-0 grid place-items-center text-xs text-slate-400">
                                        {t('sam.pictureNone')}
                                    </span>
                                )}
                                {/* A LIVE PREVIEW: this is the name and the picture
                                    as they will read, before anything is saved. */}
                                <span className="text-base font-semibold" data-testid="sam-preview-name">
                                    {(nameDraft.name ?? samName) || 'Sam'}
                                </span>
                            </div>
                            <label className="flex items-center justify-between gap-3 text-sm">
                                <span>{t('sam.nameLabel')}</span>
                                <input value={nameDraft.name ?? samName} maxLength={20}
                                       onChange={(e) => setNameDraft((d) => ({ ...d, name: e.target.value }))}
                                       data-testid="sam-name-input" className={`${field} max-w-[190px]`} />
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                {['sam', 'none'].map((pick) => (
                                    <button key={pick} type="button" data-testid={`sam-picture-${pick}`}
                                            onClick={() => setNameDraft((d) => ({ ...d, picture: pick }))}
                                            className={`${btn} ${(nameDraft.picture ?? samShownDraft) === pick
                                                ? 'bg-emerald-600 text-white border-emerald-600' : ''}`}>
                                        {pick === 'sam' ? t('sam.pictureSam') : t('sam.pictureNone')}
                                    </button>
                                ))}
                                <button type="button" className={btn} data-testid="sam-save"
                                        onClick={async () => {
                                            await saveFlag({
                                                sam_name: nameDraft.name ?? samName,
                                                sam_picture: nameDraft.picture ?? samShownDraft,
                                            });
                                            setNameDraft({});
                                            toast.success(t('sam.saved'));
                                        }}>
                                    {t('sam.save')}
                                </button>
                                <button type="button" className={btn} data-testid="sam-reset"
                                        onClick={async () => {
                                            await saveFlag({ sam_name: 'Sam', sam_picture: 'sam' });
                                            setNameDraft({});
                                            toast.success(t('sam.resetDone'));
                                        }}>
                                    {t('sam.reset')}
                                </button>
                            </div>
                        </div>

                        {/* ── SAM: his voice, from this device only ── */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2.5"
                             data-testid="sam-voice-settings">
                            <p className="text-sm font-semibold">{t('sam.voiceTitle')}</p>
                            {sv.grouped(voices).map((g) => (
                                <div key={g.id} className="space-y-1">
                                    <p className="text-xs font-semibold text-slate-500">{t(`sam.group.${g.id}`)}</p>
                                    {g.voices.length ? g.voices.map((v) => (
                                        <div key={v.voiceURI} className="flex items-center gap-2">
                                            <button type="button" data-testid={`sam-voice-pick-${v.voiceURI}`}
                                                    onClick={() => savePrefs({ uri: v.voiceURI })}
                                                    className={`${btn} flex-1 justify-start text-left ${
                                                        (prefs.uri || chosenVoice()?.voiceURI) === v.voiceURI
                                                            ? 'bg-emerald-600 text-white border-emerald-600' : ''}`}>
                                                {(prefs.uri || chosenVoice()?.voiceURI) === v.voiceURI ? '✓ ' : ''}
                                                {v.name}
                                            </button>
                                            <button type="button" className={btn}
                                                    data-testid={`sam-voice-sample-${v.voiceURI}`}
                                                    onClick={() => {
                                                        unlock();
                                                        sv.speak(t('sam.sample', { name: samName }), {
                                                            voice: v, rate: prefs.rate, pitch: prefs.pitch,
                                                            onStart: () => setSpeaking(true),
                                                            onEnd: () => setSpeaking(false),
                                                        });
                                                    }}>
                                                {t('sam.sampleBtn')}
                                            </button>
                                        </div>
                                    )) : (
                                        <p className="text-xs text-amber-500" data-testid={`sam-group-empty-${g.id}`}>
                                            {g.id === 'en-au' ? t('sam.noneAu')
                                                : (g.id === 'en-gb' ? t('sam.noneGb') : t('sam.noneOther'))}
                                        </p>
                                    )}
                                </div>
                            ))}
                            <label className="block text-xs text-slate-500">
                                {t('sam.speed')} {prefs.rate.toFixed(2)}
                                <input type="range" min="0.75" max="1.15" step="0.05" value={prefs.rate}
                                       data-testid="sam-rate" className="w-full"
                                       onChange={(e) => savePrefs({ rate: parseFloat(e.target.value) })} />
                            </label>
                            <label className="block text-xs text-slate-500">
                                {t('sam.pitch')} {prefs.pitch.toFixed(2)}
                                <input type="range" min="0.8" max="1.2" step="0.05" value={prefs.pitch}
                                       data-testid="sam-pitch" className="w-full"
                                       onChange={(e) => savePrefs({ pitch: parseFloat(e.target.value) })} />
                            </label>
                            <label className="flex items-center justify-between gap-3 text-sm">
                                <span>{t('sam.keepListening')}</span>
                                <input type="checkbox" checked={prefs.keepListening}
                                       data-testid="sam-keep-listening"
                                       onChange={(e) => savePrefs({ keepListening: e.target.checked })}
                                       className="h-5 w-5" />
                            </label>
                            <button type="button" className={btn} data-testid="sam-voice-reset"
                                    onClick={() => { savePrefs({ ...sv.DEFAULTS }); hush(); }}>
                                {t('sam.voiceReset')}
                            </button>
                        </div>
                        <label className="flex items-center justify-between gap-3 text-sm">
                            <span>{t('helper.forOrganization')}</span>
                            <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                                    data-testid="helper-org-select" className={`${field} max-w-[190px]`}>
                                <option value="">{t('helper.platformDefault')}</option>
                                {orgList.map((o) => (
                                    <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                            </select>
                        </label>
                        <div className="flex flex-wrap items-center gap-2">
                            <input value={orgFind} onChange={(e) => setOrgFind(e.target.value)}
                                   placeholder={t('helper.searchOrganizations')}
                                   data-testid="helper-org-search"
                                   className={`${field} flex-1 min-w-[150px]`} />
                            <button type="button" className={btn} data-testid="helper-org-show-test"
                                    onClick={() => setShowTest((v) => !v)}>
                                {showTest ? t('helper.hideTestOrganizations')
                                    : t('helper.showTestOrganizations')}
                            </button>
                        </div>
                        <label className="flex items-center justify-between gap-3 text-sm">
                            <span>{t('helper.enabledHere')}</span>
                            <button type="button" className={btn} data-testid="helper-toggle-enabled"
                                    onClick={() => saveFlag({ enabled: !cfg.enabled })}>
                                {cfg.enabled ? t('helper.on') : t('helper.off_')}
                            </button>
                        </label>
                        <label className="flex items-center justify-between gap-3 text-sm">
                            <span>{t('helper.hideInDemo')}</span>
                            <button type="button" className={btn} data-testid="helper-toggle-demo"
                                    onClick={() => saveFlag({ hide_in_demo: !cfg.hide_in_demo })}>
                                {cfg.hide_in_demo ? t('helper.on') : t('helper.off_')}
                            </button>
                        </label>
                        <p className="text-xs text-slate-500">{t('helper.settingsNote')}</p>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                    {!msgs.length && !preview && !run && (
                        <p className="text-sm text-slate-500" data-testid="helper-hint">
                            {t('helper.hint')}
                        </p>
                    )}
                    {msgs.map((m, i) => (
                        <div key={i} className={m.role === 'me' ? 'text-right' : ''}>
                            {m.text && (
                                <span className="inline-block rounded-2xl bg-emerald-600 text-white px-3.5 py-2 text-sm max-w-[85%] text-left"
                                      data-testid="helper-msg-me">{m.text}</span>
                            )}
                            {m.note && (
                                <p className="text-sm text-slate-500" data-testid="helper-msg-note">{m.note}</p>
                            )}
                            {m.entry && <Steps entry={m.entry} t={t} />}
                            {m.askField && (
                                <div data-testid="helper-msg-ask">
                                    <p className="text-sm mb-1.5">
                                        {m.askField === 'organization'
                                            ? t('helper.whichOrganization') : t('helper.whichMode')}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {(m.options || []).slice(0, 12).map((o) => (
                                            <button key={o} type="button" className={btn}
                                                    onClick={() => ask(m.askField === 'organization'
                                                        ? `${msgs.filter((x) => x.role === 'me').slice(-1)[0]?.text || ''} — ${o}`
                                                        : `${msgs.filter((x) => x.role === 'me').slice(-1)[0]?.text || ''} — ${o}`)}>
                                                {o}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {preview && (
                        <div className="rounded-2xl border border-amber-500/50 p-3.5 space-y-2.5"
                             data-testid="helper-preview">
                            <p className="text-sm font-semibold">
                                {t('helper.previewTitle', {
                                    count: preview.totals?.will_change || 0,
                                    organization: preview.organization.name,
                                })}
                            </p>
                            <div className="max-h-56 overflow-y-auto space-y-1">
                                {(preview.rows || []).map((r) => (
                                    <p key={r.id} className="text-xs flex justify-between gap-2"
                                       data-testid={`helper-preview-row-${r.card_id}`}>
                                        <span className="font-mono">{r.card_id}</span>
                                        <span className="text-slate-500">{r.before} → {r.after}</span>
                                    </p>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <button type="button" className={primary} disabled={busy}
                                        onClick={apply} data-testid="helper-apply">
                                    {t('helper.apply')}
                                </button>
                                <button type="button" className={btn} disabled={busy}
                                        onClick={() => { setPreview(null); setPending(null); }}
                                        data-testid="helper-cancel">
                                    {t('helper.cancel')}
                                </button>
                            </div>
                        </div>
                    )}

                    {run && (
                        <div className="rounded-2xl border border-emerald-500/50 p-3.5 space-y-2.5"
                             data-testid="helper-result">
                            <p className="text-sm font-semibold">
                                {t('helper.resultTitle', { count: run.updated_count || 0 })}
                            </p>
                            <div className="max-h-56 overflow-y-auto space-y-1">
                                {(run.rows || []).map((r) => (
                                    <p key={r.card} className="text-xs flex items-center justify-between gap-2">
                                        <span className="font-mono">{r.card_id}</span>
                                        {r.undone ? (
                                            <span className="text-slate-500" data-testid={`helper-row-undone-${r.card_id}`}>
                                                {t('helper.backTo', { mode: r.before })}
                                            </span>
                                        ) : (
                                            <button type="button" className={btn} disabled={busy}
                                                    onClick={() => undo(r.card)}
                                                    data-testid={`helper-undo-${r.card_id}`}>
                                                {t('helper.undoOne')}
                                            </button>
                                        )}
                                    </p>
                                ))}
                            </div>
                            {run.rows?.some((r) => !r.undone) && (
                                <button type="button" className={primary} disabled={busy}
                                        onClick={() => undo(null)} data-testid="helper-undo-all">
                                    <Undo2 className="w-4 h-4 inline mr-1.5" aria-hidden="true" />
                                    {t('helper.undoAll')}
                                </button>
                            )}
                        </div>
                    )}
                    {gift && (
                        <div className="rounded-2xl border border-amber-500/50 p-3.5 space-y-3"
                             data-testid="helper-gift-draft">
                            <p className="text-sm font-semibold">{t('helper.giftDraftTitle')}</p>
                            {/* the gift as the guest meets it */}
                            <div className="rounded-xl bg-slate-950 text-white px-4 py-5 text-center">
                                <p className="text-3xl font-bold" data-testid="helper-gift-amount">
                                    {gift.form.gift_type === 'monetary'
                                        ? `$${Number(gift.form.value || 0)}` : '🎁'}
                                </p>
                                <p className="text-sm mt-1 text-slate-300" data-testid="helper-gift-name">
                                    {gift.form.title || t('helper.giftNoName')}
                                </p>
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-xs">
                                    <span className="w-24 text-slate-500">{t('helper.giftFieldName')}</span>
                                    <input value={gift.form.title} className={`${field} flex-1`}
                                           data-testid="helper-gift-input-title"
                                           onChange={(e) => setGift((g) => ({ ...g, form: { ...g.form, title: e.target.value } }))} />
                                </label>
                                <label className="flex items-center gap-2 text-xs">
                                    <span className="w-24 text-slate-500">{t('helper.giftFieldAmount')}</span>
                                    <input value={gift.form.value} type="number" className={`${field} w-28`}
                                           data-testid="helper-gift-input-value"
                                           onChange={(e) => setGift((g) => ({ ...g, form: { ...g.form, value: e.target.value, gift_type: Number(e.target.value) ? 'monetary' : 'non_monetary' } }))} />
                                </label>
                                <label className="flex items-center gap-2 text-xs">
                                    <span className="w-24 text-slate-500">{t('helper.giftFieldQuantity')}</span>
                                    <input value={gift.form.quantity} type="number" className={`${field} w-28`}
                                           data-testid="helper-gift-input-quantity"
                                           onChange={(e) => setGift((g) => ({ ...g, form: { ...g.form, quantity: e.target.value } }))} />
                                    {gift.draft.quantity_is_default && (
                                        <span className="text-[11px] text-slate-500">{t('helper.giftQuantityDefault')}</span>
                                    )}
                                </label>
                                <p className="text-xs text-slate-500" data-testid="helper-gift-fields">
                                    {t('helper.giftWhere', { organization: gift.organization.name, setup: gift.setup.name })}
                                </p>
                                <p className="text-[11px] text-slate-500">{t('helper.giftNoExpiry')}</p>
                            </div>
                            <div className="flex gap-2">
                                <button type="button" className={primary} disabled={busy || !gift.form.title.trim()}
                                        data-testid="helper-gift-save"
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                const res = await api.post('/helper/action/gift-card/save', {
                                                    organization_id: gift.organization.id,
                                                    setup_id: gift.form.setup_id,
                                                    title: gift.form.title,
                                                    value: Number(gift.form.value) || 0,
                                                    gift_type: gift.form.gift_type,
                                                    quantity: Number(gift.form.quantity) || 1,
                                                    description: gift.form.description || '',
                                                });
                                                setGiftSaved(res.data);
                                                setGift(null);
                                                toast.success(t('helper.applied'));
                                            } catch (e) {
                                                toast.error(t('helper.unknown'));
                                            } finally { setBusy(false); }
                                        }}>
                                    {t('helper.save')}
                                </button>
                                <button type="button" className={btn} disabled={busy}
                                        onClick={() => setGift(null)} data-testid="helper-gift-cancel">
                                    {t('helper.cancel')}
                                </button>
                            </div>
                        </div>
                    )}

                    {giftSaved && (
                        <div className="rounded-2xl border border-emerald-500/50 p-3.5 space-y-2"
                             data-testid="helper-gift-result">
                            <p className="text-sm font-semibold">
                                {t('helper.giftSaved', { name: giftSaved.gift.title })}
                            </p>
                            <p className="text-xs text-slate-500">
                                {t('helper.whereToFind')}: {giftSaved.where} ({giftSaved.setup_name})
                            </p>
                            {giftSaved.undone ? (
                                <p className="text-xs text-slate-500" data-testid="helper-gift-undone">
                                    {t('helper.giftUndone')}
                                </p>
                            ) : (
                                <button type="button" className={primary} disabled={busy}
                                        data-testid="helper-gift-undo"
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                await api.post('/helper/action/gift-card/undo',
                                                    { run_id: giftSaved.run_id });
                                                setGiftSaved((g) => ({ ...g, undone: true }));
                                                toast.success(t('helper.giftUndone'));
                                            } finally { setBusy(false); }
                                        }}>
                                    <Undo2 className="w-4 h-4 inline mr-1.5" aria-hidden="true" />
                                    {t('helper.undoOne')}
                                </button>
                            )}
                        </div>
                    )}
                    {survey && (
                        <div className="rounded-2xl border border-amber-500/50 p-3.5 space-y-3"
                             data-testid="helper-survey-draft">
                            <p className="text-sm font-semibold">{t('helper.surveyDraftTitle')}</p>
                            {/* the survey as a guest meets it */}
                            <div className="rounded-xl bg-slate-950 text-white px-4 py-4 space-y-2">
                                <p className="text-base font-semibold" data-testid="helper-survey-name">
                                    {survey.form.name || t('helper.surveyNoName')}
                                </p>
                                {survey.form.questions.map((q, i) => (
                                    <div key={i} className="text-sm text-slate-300">
                                        <span>{i + 1}. {q}</span>
                                        <span className="block text-[11px] text-slate-500">
                                            {t('helper.surveyRating')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <label className="flex items-center gap-2 text-xs">
                                <span className="w-24 text-slate-500">{t('helper.surveyFieldName')}</span>
                                <input value={survey.form.name} className={`${field} flex-1`}
                                       data-testid="helper-survey-input-name"
                                       onChange={(e) => setSurvey((s) => ({ ...s, form: { ...s.form, name: e.target.value } }))} />
                            </label>
                            {survey.form.questions.map((q, i) => (
                                <div key={i} className="flex items-center gap-1.5">
                                    <input value={q} className={`${field} flex-1`}
                                           data-testid={`helper-survey-q-${i}`}
                                           onChange={(e) => setSurvey((s) => {
                                               const qs = [...s.form.questions];
                                               qs[i] = e.target.value;
                                               return { ...s, form: { ...s.form, questions: qs } };
                                           })} />
                                    <button type="button" className={btn} aria-label={t('helper.surveyUp')}
                                            data-testid={`helper-survey-up-${i}`} disabled={i === 0}
                                            onClick={() => setSurvey((s) => {
                                                const qs = [...s.form.questions];
                                                [qs[i - 1], qs[i]] = [qs[i], qs[i - 1]];
                                                return { ...s, form: { ...s.form, questions: qs } };
                                            })}>↑</button>
                                    <button type="button" className={btn} aria-label={t('helper.surveyRemove')}
                                            data-testid={`helper-survey-remove-${i}`}
                                            onClick={() => setSurvey((s) => ({
                                                ...s,
                                                form: { ...s.form, questions: s.form.questions.filter((_, k) => k !== i) },
                                            }))}>×</button>
                                </div>
                            ))}
                            <p className="text-[11px] text-slate-500">{t('helper.surveyDraftNote')}</p>
                            {survey.location && (
                                <p className="text-[11px] text-slate-500" data-testid="helper-survey-location">
                                    {t('helper.surveyLocation', { location: survey.location.name })}
                                </p>
                            )}
                            <div className="flex gap-2">
                                <button type="button" className={primary}
                                        disabled={busy || !survey.form.name.trim() || !survey.form.questions.length}
                                        data-testid="helper-survey-save"
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                const res = await api.post('/helper/action/survey/save', {
                                                    organization_id: survey.organization.id,
                                                    name: survey.form.name,
                                                    questions: survey.form.questions,
                                                    lang: survey.form.lang,
                                                });
                                                setSurveySaved(res.data);
                                                setSurvey(null);
                                                toast.success(t('helper.applied'));
                                            } catch (e) {
                                                toast.error(t('helper.unknown'));
                                            } finally { setBusy(false); }
                                        }}>
                                    {t('helper.surveySaveDraft')}
                                </button>
                                <button type="button" className={btn} disabled={busy}
                                        onClick={() => setSurvey(null)} data-testid="helper-survey-cancel">
                                    {t('helper.cancel')}
                                </button>
                            </div>
                        </div>
                    )}

                    {surveySaved && (
                        <div className="rounded-2xl border border-emerald-500/50 p-3.5 space-y-2"
                             data-testid="helper-survey-result">
                            <p className="text-sm font-semibold">
                                {t('helper.surveySaved', { name: surveySaved.name })}
                            </p>
                            <p className="text-xs text-slate-500">
                                {t('helper.whereToFind')}: {surveySaved.where}
                            </p>
                            {surveySaved.undone ? (
                                <p className="text-xs text-slate-500" data-testid="helper-survey-undone">
                                    {t('helper.surveyUndone')}
                                </p>
                            ) : (
                                <button type="button" className={primary} disabled={busy}
                                        data-testid="helper-survey-undo"
                                        onClick={async () => {
                                            setBusy(true);
                                            try {
                                                const res = await api.post('/helper/action/survey/undo',
                                                    { run_id: surveySaved.run_id });
                                                if (res.data.ok) {
                                                    setSurveySaved((s) => ({ ...s, undone: true }));
                                                    toast.success(t('helper.surveyUndone'));
                                                } else {
                                                    toast.warning(t('helper.undoSkipped'));
                                                }
                                            } finally { setBusy(false); }
                                        }}>
                                    <Undo2 className="w-4 h-4 inline mr-1.5" aria-hidden="true" />
                                    {t('helper.undoOne')}
                                </button>
                            )}
                        </div>
                    )}
                    <div ref={endRef} />
                </div>

                {/* My words as they arrive, what he is doing, and the rest of a
                    long answer behind one tap. */}
                {(listening || heard) && (
                    <p className="px-4 pb-1 text-sm italic text-slate-500" data-testid="sam-heard">
                        {heard || t('sam.listening')}
                    </p>
                )}
                {!!rest && (
                    <button type="button" data-testid="sam-read-rest" onClick={readRest}
                            className="mx-4 mb-2 text-xs underline text-emerald-700 dark:text-emerald-400 min-h-[44px] text-left">
                        {t('sam.readRest')}
                    </button>
                )}
                {!!micNote && (
                    <p className="px-4 pb-2 text-xs text-amber-500" data-testid="sam-mic-note">{micNote}</p>
                )}

                <form className="flex items-center gap-2 px-4 py-3 border-t border-slate-200 dark:border-slate-700"
                      onSubmit={(e) => { e.preventDefault(); unlock(); ask(); }}>
                    <input value={text} onChange={(e) => setText(e.target.value)}
                           placeholder={t('helper.placeholder')} data-testid="helper-input"
                           className={`${field} flex-1`} />
                    <button type="submit" className={primary} disabled={busy}
                            data-testid="helper-send" aria-label={t('helper.send')}>
                        <Send className="w-4 h-4" aria-hidden="true" />
                    </button>
                </form>
            </aside>
        </>
    );
};

export default HelperPanel;
